/**
 * M7 S34: the bundle picker's model (spec §2.5, §8.1; break-it EX-2). Shown at a hand-in only (S39's finale flow
 * opens it); pure, the host carries out the hand-in.
 *
 * - **Rows:** every entry the player could pick (`bundle.ts availableEntries`: items, then logs, then repeatable
 *   instances), one row per entry key, so a story item and its contents log are one row (the original); then
 *   *Hand it over*.
 * - **Picking:** activate on a row adds it (`addToBundle`) or takes it out (`removeFromBundle`). At most
 *   `case.json bundleMax`; a full bundle refuses a new entry with `bundle.full` (never a count).
 * - **The opposition's line:** for every link the bundle doesn't touch (`case.ts missingLinks`), the line the other
 *   side will say, previewed before the hand-in.
 * - **Keyboard and pad:** up and down move; activate picks or hands over; back leaves without handing over.
 */
import type { Career } from '../../../career/types'
import { addToBundle, availableEntries, bundleEntryKey, parseInstance, removeFromBundle, type CaseData } from '../../../story/bundle'
import { missingLinks } from '../../../story/case'
import { storyOf } from '../../../story/state'
import type { BundleEntry } from '../../../story/types'
import type { NavInput } from '../../strategic/mount'
import type { ClaimPanelConfig } from './claim'

export interface BundleRow {
  key: string
  entry: BundleEntry
  title: string
  /** `original` or `copy` for a story item; null for a log. */
  note: string | null
  picked: boolean
}

export interface BundleView {
  title: string
  intro: string
  rows: readonly BundleRow[]
  /** The opposition's lines for the links the bundle leaves out (empty: every link covered). */
  opposition: readonly string[]
  full: boolean
}

export interface BundleState {
  picked: readonly BundleEntry[]
  /** A row index, or `rows.length` for *Hand it over*. */
  row: number
  notice: string | null
}

export type BundleAction = { type: 'nav'; input: NavInput } | { type: 'press'; index: number } | { type: 'handIn' }

export type BundleEffect = { kind: 'handIn'; entries: BundleEntry[] } | { kind: 'close' }

export interface BundleStep {
  state: BundleState
  effect: BundleEffect | null
}

/** The title a bundle entry shows: an item by its contents log, an instance by its template. */
export function entryTitle(entry: BundleEntry, data: CaseData): string {
  if (entry.kind === 'item') {
    for (const log of data.logsById.values()) if (log.item === entry.id) return log.title
    return entry.id
  }
  const log = data.logsById.get(entry.id)
  if (log !== undefined) return log.title
  const hash = entry.id.lastIndexOf('#')
  const template = parseInstance(entry.id, data.case) !== null ? data.logsById.get(entry.id.slice(0, hash)) : undefined
  return template?.title ?? entry.id
}

export function createBundleState(picked: readonly BundleEntry[] = []): BundleState {
  return { picked: [...picked], row: 0, notice: null }
}

export function buildBundleView(career: Career, s: BundleState, data: CaseData, cfg: ClaimPanelConfig): BundleView {
  const b = cfg.bundle
  const pickedKeys = new Set(s.picked.map((e) => bundleEntryKey(e, data.logsById)))
  const items = storyOf(career).items
  const seen = new Set<string>()
  const rows: BundleRow[] = []
  for (const entry of availableEntries(career, data)) {
    const key = bundleEntryKey(entry, data.logsById)
    if (seen.has(key)) continue
    seen.add(key)
    const state = entry.kind === 'item' ? items.find((i) => i.id === entry.id)?.state : undefined
    const note = state === undefined ? null : state === 'copy' ? b.copy : b.original
    rows.push({ key, entry, title: entryTitle(entry, data), note, picked: pickedKeys.has(key) })
  }
  const opposition = missingLinks(career, s.picked, data).map((l) => b.opposition[l])
  return { title: b.title, intro: b.intro, rows, opposition, full: s.picked.length >= data.case.bundleMax }
}

function press(career: Career, s: BundleState, view: BundleView, index: number, data: CaseData, cfg: ClaimPanelConfig): BundleStep {
  if (index >= view.rows.length) return { state: { ...s, row: view.rows.length }, effect: { kind: 'handIn', entries: [...s.picked] } }
  const row = view.rows[index]
  if (row === undefined) return { state: s, effect: null }
  if (row.picked) return { state: { ...s, row: index, notice: null, picked: removeFromBundle(s.picked, row.entry, data) }, effect: null }
  const r = addToBundle(career, s.picked, row.entry, data)
  if (!r.ok) return { state: { ...s, row: index, notice: r.reason === 'full' ? cfg.bundle.full : cfg.bundle.unavailable }, effect: null }
  return { state: { ...s, row: index, notice: null, picked: r.entries }, effect: null }
}

export function reduceBundle(career: Career, s: BundleState, a: BundleAction, view: BundleView, data: CaseData, cfg: ClaimPanelConfig): BundleStep {
  const last = view.rows.length
  switch (a.type) {
    case 'press':
      return press(career, s, view, a.index, data, cfg)
    case 'handIn':
      return press(career, s, view, last, data, cfg)
    case 'nav':
      break
  }
  switch (a.input) {
    case 'back':
    case 'start':
      return { state: s, effect: { kind: 'close' } }
    case 'up':
      return { state: { ...s, row: Math.max(0, s.row - 1) }, effect: null }
    case 'down':
      return { state: { ...s, row: Math.min(last, s.row + 1) }, effect: null }
    case 'activate':
      return press(career, s, view, s.row, data, cfg)
    case 'left':
    case 'right':
      return { state: s, effect: null }
  }
}
